import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import config from '../config/config';

const features = [
    {
        title: '智能家谱',
        desc: '安全私密',
        color: 'from-green-500 to-emerald-600',
        bg: 'bg-green-50',
        text: 'text-green-600',
        icon: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        ),
    },
    {
        title: '成长动态',
        desc: '记录童年',
        color: 'from-blue-500 to-indigo-600',
        bg: 'bg-blue-50',
        text: 'text-blue-600',
        icon: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        ),
    },
    {
        title: '人生传记',
        desc: '爱意永续',
        color: 'from-orange-500 to-amber-600',
        bg: 'bg-orange-50',
        text: 'text-orange-600',
        icon: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        ),
    },
    {
        title: '时光私信',
        desc: '亲情传递',
        color: 'from-purple-500 to-pink-600',
        bg: 'bg-purple-50',
        text: 'text-purple-600',
        icon: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        ),
    },
];

const Intro = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const sectionRef = useRef(null);
    const introImages = config.introImages;

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { rootMargin: '100px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        const timer = setInterval(() => {
            setCurrentIndex(i => (i + 1) % introImages.length);
        }, 3000);
        return () => clearInterval(timer);
    }, [introImages.length, isVisible]);

    return (
        <div id="about" ref={sectionRef} className="bg-gradient-to-br from-white to-blue-50/60 py-16 md:py-24">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
                    {/* 文字内容区域 */}
                    <div className="flex flex-col w-full lg:w-1/2">
                        {/* 标题装饰 */}
                        <div className="flex items-center mb-5">
                            <div className="w-10 h-1 bg-blue-600 rounded-full mr-3"></div>
                            <span className="text-blue-600 font-semibold tracking-wider text-sm">关于我们</span>
                        </div>

                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 leading-tight mb-6">
                            存续家族记忆，传承血脉<span className="text-blue-600">亲情</span>
                        </h2>

                        <div className="space-y-4 mb-8">
                            <p className="text-lg text-gray-600 leading-relaxed">
                                我们致力于让数字技术链接亲情，使家的故事在数字世界薪火相传，生生不息。
                            </p>

                            <div className="bg-white rounded-xl p-5 border-l-4 border-blue-500 shadow-sm">
                                <p className="text-gray-700 font-medium leading-relaxed">
                                    我们的使命是让家的记忆永不消逝，在数字时代让亲情连接更加紧密。
                                </p>
                            </div>

                            <p className="text-lg text-gray-600 leading-relaxed">
                                守护家庭记忆，延续血脉亲情，让家的故事世代相传。
                            </p>
                            <p className="text-blue-600 font-semibold">
                                —— 族语APP，让家的记忆不再遗失。
                            </p>
                        </div>

                        {/* 功能亮点 */}
                        <div className="grid grid-cols-2 gap-3 mb-8">
                            {features.map((f, i) => (
                                <div
                                    key={i}
                                    className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                                >
                                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${f.color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            {f.icon}
                                        </svg>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-gray-800 text-sm leading-tight">{f.title}</p>
                                        <p className={`text-xs ${f.text}`}>{f.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* CTA 按钮 */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link
                                to="/contact"
                                className="bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center justify-center px-8 py-3.5 text-base font-medium shadow-lg shadow-blue-200 rounded-xl transition-all duration-300 group"
                            >
                                下载族语
                                <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"></path>
                                </svg>
                            </Link>
                            <Link
                                to="/app"
                                className="bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 inline-flex items-center justify-center px-8 py-3.5 text-base font-medium shadow-sm rounded-xl transition-all duration-300"
                            >
                                了解更多
                            </Link>
                        </div>
                    </div>

                    {/* 图片轮播区域 */}
                    <div className="w-full lg:w-1/2 flex justify-center">
                        <div className="relative w-full max-w-md">
                            {/* 背景装饰：blur 在手机上极耗 GPU，仅桌面端显示 */}
                            <div className="hidden md:block absolute -top-6 -right-6 w-40 h-40 bg-blue-200/40 rounded-full blur-2xl"></div>
                            <div className="hidden md:block absolute -bottom-6 -left-6 w-40 h-40 bg-purple-200/40 rounded-full blur-2xl"></div>

                            <div className="relative bg-white rounded-3xl p-5 shadow-xl border border-gray-100">
                                <div className="rounded-2xl overflow-hidden bg-gray-50">
                                    <img
                                        src={introImages[currentIndex]}
                                        alt={`族语App界面 ${currentIndex + 1}`}
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-auto max-h-[560px] object-contain mx-auto"
                                    />
                                </div>
                                {/* 指示点 */}
                                <div className="flex justify-center gap-2 mt-4">
                                    {introImages.map((_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setCurrentIndex(i)}
                                            aria-label={`切换到第 ${i + 1} 张`}
                                            className={`h-2 rounded-full transition-all duration-300 ${i === currentIndex ? 'bg-blue-600 w-6' : 'bg-gray-300 w-2'}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Intro;
